import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { twMerge } from 'tailwind-merge';
import type { z } from 'zod';

import { TextStyle } from '../../../../../DesignSystem/Feedback/TextStyle';
import { Tooltip } from '../../../../../DesignSystem/Feedback/Tooltip';
import { Checkbox } from '../../../../../DesignSystem/Inputs/Checkbox';
import { NestedDropdownMenu } from '../../../../../DesignSystem/Inputs/NestedDropdownMenu';
import { TextField } from '../../../../../DesignSystem/Inputs/TextField';
import { ToggleSwitch } from '../../../../../DesignSystem/Inputs/ToggleSwitch';
import { messages } from '../message';
import { getDefaultValues, getSchema } from '../schema';

const inputClassName = 'mt-1 placeholder:text-gray-8 text-sm';
const labelClassName = 'text-sm font-medium';

export function SettingsAndControls() {
  const { formatMessage } = useIntl();
  const totalClaimLimitOptions = useMemo(
    () => [
      {
        value: 'unlimited',
        label: formatMessage(messages.unlimited),
      },
      {
        value: 'limited',
        label: formatMessage(messages.limited),
      },
    ],
    [formatMessage]
  );
  const schema = getSchema(formatMessage);
  type FormFields = z.infer<typeof schema>;
  const {
    control,
    register,
    getValues,
    watch,
    formState: { errors },
    setValue,
  } = useFormContext<FormFields>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'proof.content',
  });
  const isProofRequired = watch('proof.isRequired');
  const watchFieldArray = watch('proof.content');
  const watchIndividualClaimLimit = watch('individualClaimLimit');
  const { onChange, name, ref } = register('individualClaimLimit', {
    valueAsNumber: true,
  });

  const controlledFields = fields.map((field, index) => {
    return {
      ...field,
      ...watchFieldArray[index],
    };
  });
  const [claimLimit, setClaimLimit] = useState(totalClaimLimitOptions[0]);

  useEffect(() => {
    if (getValues('totalClaimLimit') > -1) {
      setClaimLimit(totalClaimLimitOptions[1]);
    } else {
      setClaimLimit(totalClaimLimitOptions[0]);
    }
  }, [getValues, totalClaimLimitOptions]);

  const updateClaimLimit = (option: { value: string; label: string }) => {
    setClaimLimit(option);
    if (option.value === 'limited') {
      setValue('totalClaimLimit', 1);
    } else {
      setValue('totalClaimLimit', -1);
    }
  };

  const onProofCheckboxChange = () => {
    // disable isRequired to false if all proofs are unchecked
    const areAllProofsDisabled = getValues('proof.content').every(
      (field) => !field.isChecked
    );
    if (areAllProofsDisabled) {
      setValue('proof.isRequired', false);
    }
  };

  const onRequiredProofChange = (isChecked: boolean) => {
    // when user disables all proof checkboxes and then enable proof required toggle, prefill default values
    if (isChecked) {
      const defaultValue = getDefaultValues(formatMessage).proof.content;
      const areAllProofsDisabled = getValues('proof.content').every(
        (field) => !field.isChecked
      );
      if (areAllProofsDisabled) {
        remove();
        append(defaultValue);
      }
    }
  };

  return (
    <>
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1">
            <TextStyle as="span" variant="sm-medium" className="text-gray-9">
              {formatMessage(messages.totalClaimLimitTitle)}
            </TextStyle>
            <Tooltip
              tooltipText={formatMessage(messages.totalLimitToolTip)}
              contentClassName="relative z-[200]"
              side="top"
            >
              <InformationCircleIcon className="h-4 w-4" tabIndex={0} />
            </Tooltip>
          </div>

          <div className="flex items-baseline gap-2">
            <NestedDropdownMenu
              className="w-52 grow"
              contentClassName={
                claimLimit.value === 'unlimited' ? 'w-[520px]' : ''
              }
              alignmentOptions={{
                align: 'start',
              }}
              selectedValue={claimLimit}
              options={totalClaimLimitOptions}
              onSelected={updateClaimLimit}
              placeholder={'Select claim Limit'}
            />
            {claimLimit.value === 'limited' && (
              <>
                <TextField
                  {...register('totalClaimLimit', {
                    valueAsNumber: true,
                  })}
                  type="number"
                  className="w-52"
                  invalidText=""
                  isInvalid={false}
                  step="1"
                  min="1"
                  inputClassName={inputClassName}
                  labelClassName={labelClassName}
                  aria-label="totalClaimLimit"
                  onBlur={(e) => {
                    if (Number(e.target.value) < 1) {
                      setValue('totalClaimLimit', 1);
                    }
                  }}
                />
                <TextStyle
                  as="span"
                  variant="sm-regular"
                  className="text-primary"
                >
                  {formatMessage(messages.totalClaims)}
                </TextStyle>
              </>
            )}
          </div>
          <TextStyle as="span" className="text-gray-8" variant="xs-regular">
            {formatMessage(messages.totalClaimLimitHintLabel)}
          </TextStyle>
        </div>
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1">
            <TextStyle as="span" variant="sm-medium">
              {formatMessage(messages.individualClaimLimitTitle)}
            </TextStyle>
            <Tooltip
              tooltipText={formatMessage(messages.individualClaimLimitTooltip)}
              contentClassName="relative z-[200]"
              side="top"
            >
              <InformationCircleIcon className="h-4 w-4" tabIndex={0} />
            </Tooltip>
          </div>

          <div className="flex items-center gap-3">
            <TextField
              onChange={onChange}
              onBlur={(e) => {
                if (Number(e.target.value) > 500) {
                  setValue('individualClaimLimit', 500);
                } else if (Number(e.target.value) < 1) {
                  setValue('individualClaimLimit', 1);
                }
              }}
              name={name}
              ref={ref}
              type="number"
              className="w-52"
              invalidText=""
              isInvalid={watchIndividualClaimLimit > 500}
              step="1"
              min="1"
              max="500"
              inputClassName={inputClassName}
              labelClassName={labelClassName}
              aria-label="individualClaimLimit"
            />
            <TextStyle as="span" variant="sm-regular">
              {formatMessage(messages.numberOfTimes)}
            </TextStyle>
          </div>
          <TextStyle as="span" className="text-gray-8" variant="xs-regular">
            {formatMessage(messages.individualClaimLimitHintLabel)}
          </TextStyle>
        </div>
      </div>
      <hr className="my-4 text-gray-5" />
      <div>
        <div className="flex justify-between pb-3">
          <TextStyle as="span" variant="sm-medium" className="text-gray-9">
            {formatMessage(messages.proofRequired)}
          </TextStyle>

          <Controller
            defaultValue={false}
            name="proof.isRequired"
            control={control}
            render={({ field: { onChange, value } }) => (
              <ToggleSwitch
                checked={value}
                onCheckedChange={(isChecked) => {
                  onChange(isChecked);
                  onRequiredProofChange(isChecked);
                }}
                aria-label={'proofRequired'}
              />
            )}
          />
        </div>
        {Boolean(isProofRequired) && (
          <>
            <TextStyle as="span" variant="sm-medium" className="text-gray-9">
              {formatMessage(messages.typesQuestion)}
            </TextStyle>
            <div className="flex flex-col gap-3 py-1">
              {controlledFields.map((item, index) => {
                const error =
                  errors.proof?.content?.[index]?.promptText?.message ?? '';
                const title = getValues(
                  `proof.content.${index}.type`
                ).toLowerCase();
                return (
                  <div key={index}>
                    <div className="flex items-center gap-2 py-2">
                      <div>
                        <Controller
                          defaultValue={false}
                          name={`proof.content.${index}.isChecked`}
                          control={control}
                          render={({ field: { onChange, value } }) => (
                            <Checkbox
                              className={
                                index === 1
                                  ? 'cursor-not-allowed'
                                  : 'cursor-pointer'
                              }
                              disabled={index === 1 && true}
                              id={item.id}
                              checked={value}
                              onChange={(e) => {
                                onChange(e);
                                if (!e.target.checked) {
                                  setValue(
                                    `proof.content.${index}.promptText`,
                                    ''
                                  );
                                }
                                onProofCheckboxChange();
                              }}
                            />
                          )}
                        />
                        <label
                          htmlFor={item.id}
                          className={twMerge(
                            'ml-2 text-sm text-gray-9',
                            index === 1
                              ? 'cursor-not-allowed'
                              : 'cursor-pointer'
                          )}
                        >
                          {formatMessage(
                            messages[title as keyof typeof messages]
                          )}
                        </label>
                        {index === 1 && (
                          <TextStyle
                            as="span"
                            variant="sm-regular"
                            className="m-2 rounded-md bg-gray-3 p-1"
                          >
                            {formatMessage(messages.comingSoon)}
                          </TextStyle>
                        )}
                      </div>
                    </div>
                    {Boolean(getValues(`proof.content.${index}.isChecked`)) && (
                      <TextField
                        {...register(`proof.content.${index}.promptText`)}
                        type="text"
                        invalidText={error}
                        label={formatMessage(messages.customisePrompt)}
                        isInvalid={Boolean(error)}
                        inputClassName={inputClassName}
                        labelClassName={labelClassName}
                        aria-label={item.id}
                        helpText={formatMessage(messages.proofHintLabel)}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </>
  );
}
